import { useCallback, useEffect, useRef, useState } from 'react';
import { logBackgroundError } from '../observability';

/** One list of pending requests that the page reads and prunes (#664, F16). */
export interface Queue<T> {
  rows: T[];
  /** A read is in flight. It starts `true`, so the empty text does not flash before the first answer. */
  loading: boolean;
  /** The last read failed (#457). The page shows an error state, never an empty list. */
  failed: boolean;
  /** Read the list again. */
  reload: () => Promise<void>;
  /** A local write: the page removes a resolved row. A read that began before the write is read again. */
  setRows: (next: T[] | ((prev: T[]) => T[])) => void;
}

/**
 * The state of one request queue of the Admin page: the rows, the loading
 * flag and the failed flag (#664, F16). The page had six copies of
 * "set loading, read, set rows, mark the failure, clear loading".
 *
 * `source` names the queue in the telemetry of a failed read. A disabled queue
 * (`enabled: false`, for example the role queue of a team manager) reads
 * nothing: it is never loading, never failed, and empty.
 *
 * A late answer must not draw over a newer write (frontend.md, Lessons). A
 * read that began before a local write (`setRows`) is not applied: the hook
 * reads again. A read that began before a newer read is dropped.
 */
export function useQueue<T>(read: () => Promise<T[]>, opts: { source: string; enabled?: boolean }): Queue<T> {
  const enabled = opts.enabled ?? true;
  const [rows, setRowsState] = useState<T[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const reads = useRef(0);
  const writes = useRef(0);
  const latest = useRef({ read, source: opts.source, enabled });
  latest.current = { read, source: opts.source, enabled };
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(async () => {
    if (!latest.current.enabled) return;
    const run = ++reads.current;
    setLoading(true);
    try {
      for (;;) {
        const startedAt = writes.current;
        const data = await latest.current.read();
        if (!alive.current || run !== reads.current) return;
        if (writes.current !== startedAt) continue;
        setRowsState(data);
        setFailed(false);
        return;
      }
    } catch (err) {
      if (alive.current && run === reads.current) {
        setFailed(true);
        logBackgroundError(latest.current.source)(err);
      }
    } finally {
      if (alive.current && run === reads.current) setLoading(false);
    }
  }, []);

  const setRows = useCallback((next: T[] | ((prev: T[]) => T[])) => {
    writes.current++;
    setRowsState(next);
  }, []);

  return { rows: enabled ? rows : [], loading: enabled && loading, failed: enabled && failed, reload, setRows };
}
